/** @jsx isolateComponent */

import { Fragment, ReactNode, useState, useRef } from 'react';

import { isolateComponent } from '@zapier/common-utils';

import { TrialExtensionReason } from '@zapier/design-system-revenue/src/types';

import { UpgradeModal } from '../UpgradeModal';

type InlineUpgradeTriggerChildProps = {
  triggerModal: () => void;
  reasonKey: TrialExtensionReason;
  planName: string;
};

type Props = {
  /** The name of a Zapier integration, such as `GoogleSheetsAPI`. */
  appName?: string;
  /** When true, the modal will automatically be displayed when the component is mounted. */
  autoOpen?: boolean;
  /**
   * If `autoOpen` is false then the children components are responsible for
   * opening this modal by calling the `triggerModal` function callback.
   */
  children?: (a: InlineUpgradeTriggerChildProps) => ReactNode;
  /**
   * Called when the back button is clicked.
   * The back button is only displayed when a user is not permitted to restart a trial.
   * **If this prop is not defined then `onClose` will be called instead.**
   */
  onBack?: () => void;
  /**
   * Called when a user dismisses the modal without starting a trial or upgrading
   * their account.
   **/
  onClose: () => void;
  /** Called when a user successfully upgrades their account. */
  onUpgradeFinish: () => void;
  /**
   * An object with data about the plan to which the user is prompted to upgrade.
   */
  upgradeToPlan: any;
  /**
   * A key that denotes the reason that a user is prompted to upgrade.
   * The content of the modal changes depending on this value.
   */
  validationReason: TrialExtensionReason;
};

/**
 * Renders a modal that prompts a user to upgrade their account for a particular reason,
 * such as needing more tasks or the ability to email support.
 */
export const InlineUpgradeTrigger = ({ children, ...props }: Props) => {
  const {
    appName,
    autoOpen,
    onClose,
    onBack,
    onUpgradeFinish,
    validationReason,
    upgradeToPlan,
  } = props;

  // Memoise the new plan to avoid issues displaying the celebrate modal post-upgrade
  const newPlanRef = useRef(upgradeToPlan);

  const [isOpen, setIsOpen] = useState(autoOpen || false);
  const open = () => setIsOpen(true);

  const close = () => {
    onClose();
    setIsOpen(false);
  };

  const back = () => {
    (onBack || onClose)();
    setIsOpen(false);
  };

  return (
    <Fragment>
      {children
        ? children({
            triggerModal: open,
            reasonKey: validationReason,
            planName: newPlanRef.current?.type || newPlanRef.current?.name,
          })
        : null}
      {isOpen && (
        <UpgradeModal
          appName={appName}
          onBack={back}
          onClose={close}
          onUpgradeFinish={onUpgradeFinish}
          upgradeToPlan={newPlanRef.current}
          validationReason={validationReason}
        />
      )}
    </Fragment>
  );
};
